import { ComponentType } from 'react';
import { generatePath, Navigate, Outlet, RouteObject, To, useParams } from 'react-router-dom';
import queryString from 'query-string';

import { Loading, RobustSuspense } from '@components/base';
import { ErrorBoundary } from '@components/feedback/ErrorBoundary';
import { IndexName } from '@enums/IndexName';
import { SearchParams } from '@hooks/useLayoutEntityFilter/useFilteredLayoutEntities';
import { LayoutEntityType } from '@models/Content';
import { SearchPageQueryParams } from '@models/SearchQuery';
import { SettingsTabs } from '@modules/layouts/settings/utils';
import { ValidationSeverity } from '@modules/metadataValidation/models/ValidationFailure';
import { SimTab } from '@modules/simulations/components/Simulations';
import {
  ContentStatisticsPageTab,
  defaultContentStatisticsPageTab,
} from '@modules/statistics/utils/types';

import { robustLazy } from './utils/robustLazy';

export function getIngestSearchPath(indexName: IndexName, searchQuery?: SearchPageQueryParams): To {
  const pathname =
    indexName === IndexName.Assets ? '/ingest/search' : `/ingest/search/${indexName}`;

  return searchQuery
    ? { pathname, search: queryString.stringify(searchQuery, { arrayFormat: 'bracket' }) }
    : pathname;
}

const LazyContentStatisticsPage = robustLazy(() => import('./pages/statistics/statistics'));

// Layouts
const LazyCollectionPage = robustLazy(() => import('./pages/layouts/collections/collection'));
const LazyCreatePagePage = robustLazy(() => import('./pages/layouts/pages/create'));
const LazyPagePage = robustLazy(() => import('./pages/layouts/pages/page'));
const LazyCreateMenuPage = robustLazy(() => import('./pages/layouts/menus/create'));
const LazyMenuPage = robustLazy(() => import('./pages/layouts/menus/menu'));

// Enhanced lazy-loaded components with built-in prop injection

const LazySearchAssets = robustLazy(() => import('./pages/search'), {
  indexName: IndexName.Assets,
}) as ComponentType;

const LazySearchPlayback = robustLazy(() => import('./pages/search'), {
  indexName: IndexName.PlaybackOptions,
}) as ComponentType;

const LazyIngestSearchAssets = robustLazy(() => import('./pages/search'), {
  indexName: IndexName.Assets,
}) as ComponentType;

const LazyIngestSearchPlayback = robustLazy(() => import('./pages/search'), {
  indexName: IndexName.PlaybackOptions,
}) as ComponentType;

const LazyMetadataValidationErrors = robustLazy(
  () => import('./pages/operations/metadataValidation'),
  { tab: 'error' }
) as ComponentType;

const LazyMetadataValidationWarnings = robustLazy(
  () => import('./pages/operations/metadataValidation'),
  { tab: 'warning' }
) as ComponentType;

const LazySimulationsEntitlements = robustLazy(() => import('./pages/operations/simulations'), {
  tab: 'entitlements',
}) as ComponentType;

const LazySimulationsBookmarks = robustLazy(() => import('./pages/operations/simulations'), {
  tab: 'bookmarks',
}) as ComponentType;

const LazySimulationsProviders = robustLazy(() => import('./pages/operations/simulations'), {
  tab: 'providers',
}) as ComponentType;

const LazyCreateRootMenu = robustLazy(() => import('./pages/layouts/menus/create'), {
  isRootMenu: true,
});

const LazyRootMenuPage = robustLazy(() => import('./pages/layouts/menus/menu'), {
  isRootMenu: true,
});

const LazyRootMenuPreview = robustLazy(
  () => import('@modules/layouts/components/layoutPreview/MenuPreview'),
  { isRootMenu: true }
);

const LazyRootMenusPage = robustLazy(() => import('./pages/layouts/menus'), { isRootMenu: true });

const LegacyFastTrackChannelRedirect = () => {
  return <Navigate replace to='/operations/fast-track/channels' />;
};

const LegacyDaiRolloutRedirect = () => {
  const { id } = useParams();

  return <Navigate replace to={id ? getDaiRolloutPath(id) : getDaiRolloutsPath()} />;
};

export const getAppRoutes = (canWrite: boolean): RouteObject[] => [
  {
    errorElement: <ErrorBoundary />,
    element: (
      <RobustSuspense fallback={<Loading />}>
        <Outlet />
      </RobustSuspense>
    ),
    children: [
      { Component: robustLazy(() => import('./pages')), index: true },
      {
        path: 'search',
        children: [
          { index: true, Component: LazySearchAssets },
          { path: IndexName.PlaybackOptions, Component: LazySearchPlayback },
        ],
      },
      {
        path: 'ingest/search',
        children: [
          { index: true, Component: LazyIngestSearchAssets },
          { path: IndexName.PlaybackOptions, Component: LazyIngestSearchPlayback },
        ],
      },
      {
        path: 'ingest/summaries',
        Component: robustLazy(() => import('./pages/ingest/summaries')),
      },
      {
        path: 'ingest/validations',
        Component: robustLazy(() => import('./pages/ingest/validations')),
      },
      {
        path: 'ingest/diff',
        Component: robustLazy(() => import('./pages/ingest/diff')),
      },
      {
        path: 'ingest/settings',
        Component: robustLazy(() => import('./pages/ingest/settings')),
      },
      {
        path: 'ingest/sql-titles',
        Component: robustLazy(() => import('./pages/ingest/sqlTitles')),
      },
      {
        path: 'ingest/possible-merges/*',
        Component: robustLazy(() => import('./pages/ingest/possibleMerges')),
      },

      // LAYOUTS
      {
        path: 'layouts',
        children: [
          { Component: robustLazy(() => import('./pages/layouts')), index: true },
          //  COLLECTION ROUTES
          {
            path: 'collections',
            children: [
              {
                path: ':collectionId',
                Component: LazyCollectionPage,
              },
              { index: true, Component: robustLazy(() => import('./pages/layouts/collections')) },
            ],
          },
          // PAGE ROUTES
          {
            path: 'pages',
            children: [
              ...(canWrite
                ? [
                    {
                      path: 'create',
                      Component: LazyCreatePagePage,
                    },
                  ]
                : []),
              {
                path: ':pageId',
                children: [
                  {
                    index: true,
                    Component: LazyPagePage,
                  },
                  {
                    path: 'preview',
                    Component: robustLazy(
                      () => import('@modules/layouts/components/layoutPreview/PagePreview')
                    ),
                  },
                ],
              },
              { index: true, Component: robustLazy(() => import('./pages/layouts/pages')) },
            ],
          },
          // MENUS ROUTES
          {
            path: 'menus',
            children: [
              ...(canWrite
                ? [
                    {
                      path: 'create',
                      Component: LazyCreateMenuPage,
                    },
                  ]
                : []),
              {
                path: ':menuId',
                children: [
                  {
                    index: true,
                    Component: LazyMenuPage,
                  },
                  {
                    path: 'preview',
                    Component: robustLazy(
                      () => import('@modules/layouts/components/layoutPreview/MenuPreview')
                    ),
                  },
                ],
              },
              { index: true, Component: robustLazy(() => import('./pages/layouts/menus')) },
            ],
          },
          // MAIN MENUS ROUTES
          {
            path: 'main-menus',
            children: [
              ...(canWrite ? [{ path: 'create', Component: LazyCreateRootMenu }] : []),
              {
                path: ':menuId',
                children: [
                  { index: true, Component: LazyRootMenuPage },
                  { path: 'preview', Component: LazyRootMenuPreview },
                ],
              },
              { index: true, Component: LazyRootMenusPage },
            ],
          },
          {
            path: 'settings',
            children: [
              { index: true, Component: robustLazy(() => import('./pages/layouts/settings')) },
              ...SettingsTabs.map(tab => ({
                path: tab,
                Component: robustLazy(() => import('./pages/layouts/settings')),
              })),
            ],
          },
          { path: 'images', Component: robustLazy(() => import('./pages/layouts/images')) },
        ],
      },

      // OPERATIONS
      {
        path: 'metadata-validation',
        children: [
          { path: 'errors', Component: LazyMetadataValidationErrors },
          { path: 'warnings', Component: LazyMetadataValidationWarnings },
        ],
      },
      {
        path: 'duplicate-resolution/*',
        Component: robustLazy(() => import('./pages/operations/duplicateResolution')),
      },
      {
        path: 'operations/fast-track',
        Component: robustLazy(() => import('./pages/content/fastTrack')),
        children: [
          { index: true, element: <Navigate replace to='channels' /> },
          {
            path: 'channels',
            Component: robustLazy(() => import('./pages/content/fastTrackChannels')),
          },
          {
            path: 'rollouts',
            children: [
              { index: true, Component: robustLazy(() => import('./pages/content/daiRollouts')) },
              {
                path: 'new',
                element: <Navigate replace to={getDaiRolloutNewPath()} />,
              },
              { path: ':id', Component: LegacyDaiRolloutRedirect },
            ],
          },
          { path: ':channelId', Component: LegacyFastTrackChannelRedirect },
        ],
      },
      {
        path: 'operations/devices',
        Component: robustLazy(() => import('./pages/operations/devices')),
      },
      {
        path: 'operations/foc-subscribers',
        Component: robustLazy(() => import('./pages/operations/focSubscribers')),
      },

      // WORKFLOW ROUTES
      {
        path: 'workflows',
        children: [
          { index: true, Component: robustLazy(() => import('./pages/workflows')) },
          {
            path: ':workflowId',
            Component: robustLazy(() => import('./pages/workflows/workflow')),
          },
          {
            path: ':workflowId/:runId',
            Component: robustLazy(() => import('./pages/workflows/workflow/run')),
          },
        ],
      },

      // EPG
      {
        path: 'epg',
        children: [
          { path: ':channelId', Component: robustLazy(() => import('./pages/epg/channelEpg')) },
          { index: true, Component: robustLazy(() => import('./pages/epg')) },
        ],
      },

      // SIMULATIONS
      {
        path: 'simulations',
        children: [
          { index: true, Component: LazySimulationsEntitlements },
          { path: 'bookmarks', Component: LazySimulationsBookmarks },
          { path: 'entitlements', Component: LazySimulationsEntitlements },
          { path: 'providers', Component: LazySimulationsProviders },
        ],
      },

      // STATISTICS
      {
        path: 'statistics',
        children: [
          {
            path: 'content/:tab?',
            Component: LazyContentStatisticsPage,
          },
          {
            path: 'query-logs',
            Component: robustLazy(() => import('./pages/statistics/queryLogs')),
          },
          {
            path: 'title-popularity',
            Component: robustLazy(() => import('./pages/statistics/titlePopularity')),
          },
          {
            path: 'favorites-insights',
            Component: robustLazy(() => import('./pages/statistics/favoritesInsights')),
          },
          {
            path: 'foc',
            Component: robustLazy(() => import('./pages/statistics/foc')),
          },
        ],
      },

      {
        path: 'client-config',
        Component: robustLazy(() => import('./pages/operations/clientConfig')),
      },

      // CONTENT
      {
        path: 'content',
        children: [
          { path: 'providers', Component: robustLazy(() => import('./pages/content/providers')) },
          {
            path: 'channels',
            children: [
              { index: true, Component: robustLazy(() => import('./pages/content/channels')) },
              { path: 'new', Component: robustLazy(() => import('./pages/content/channel')) },
              { path: ':id', Component: robustLazy(() => import('./pages/content/channel')) },
            ],
          },
          {
            path: 'packages',
            children: [
              { index: true, Component: robustLazy(() => import('./pages/content/packages')) },
              { path: 'new', Component: robustLazy(() => import('./pages/content/package')) },
              { path: ':id', Component: robustLazy(() => import('./pages/content/package')) },
            ],
          },
          {
            path: 'tier-packages',
            children: [
              {
                index: true,
                Component: robustLazy(() => import('./pages/content/tieredPackages')),
              },
              {
                path: 'new',
                Component: robustLazy(() => import('./pages/content/tieredPackage')),
              },
              {
                path: ':id',
                Component: robustLazy(() => import('./pages/content/tieredPackage')),
              },
            ],
          },
          {
            path: 'bundles',
            children: [
              { index: true, Component: robustLazy(() => import('./pages/content/bundles')) },
              { path: ':id', Component: robustLazy(() => import('./pages/content/bundle')) },
            ],
          },
          {
            path: 'dai-rollouts',
            children: [
              { index: true, element: <Navigate replace to={getDaiRolloutsPath()} /> },
              {
                path: 'new',
                element: (
                  <Navigate replace to={canWrite ? getDaiRolloutNewPath() : getDaiRolloutsPath()} />
                ),
              },
              { path: ':id', Component: LegacyDaiRolloutRedirect },
            ],
          },
        ],
      },

      // DEV/TESTING
      {
        path: 'dev',
        children: [
          { path: 'components', Component: robustLazy(() => import('./pages/dev/Components')) },
          {
            path: 'chunk-load-test',
            Component: robustLazy(() => import('./pages/dev/ChunkLoadTest')),
          },
        ],
      },

      { path: '*', Component: robustLazy(() => import('./pages/404')) },
    ],
  },
];

// ROUTE UTILS

// Search page
export const getSearchPath = (indexName: IndexName, searchQuery?: SearchPageQueryParams): To => {
  const pathname = indexName === IndexName.Assets ? '/search' : `/search/${indexName}`;

  return searchQuery
    ? { pathname, search: queryString.stringify(searchQuery, { arrayFormat: 'bracket' }) }
    : pathname;
};

export const getIngestSummariesPath = () => '/ingest/summaries';
export const getIngestValidationsPath = () => '/ingest/validations';
export const getIngestDiffPath = () => '/ingest/diff';
export const getIngestSettingsPath = () => '/ingest/settings';
export const getIngestSqlTitlesPath = () => '/ingest/sql-titles';
export const getIngestPossibleMergesPath = () => '/ingest/possible-merges';
export const getIngestPossibleMergePath = (masterId: string) =>
  generatePath('/ingest/possible-merges/:masterId', { masterId });

// Layout landing page
export const getLayoutLandingPagePath = () => '/layouts';

// Settings
export const getSettingsPagePath = () => '/layouts/settings';

// Layout images
export const getImagesPagePath = () => '/layouts/images';

// Menus
export const getMenuListPath = (isRootMenu?: boolean) =>
  isRootMenu ? '/layouts/main-menus' : '/layouts/menus';

export const getCreateMenuPath = (isRootMenu?: boolean) =>
  isRootMenu ? '/layouts/main-menus/create' : '/layouts/menus/create';

export const getMenuPath = (menuId: string, isRootMenu?: boolean) =>
  isRootMenu
    ? generatePath('/layouts/main-menus/:menuId', { menuId })
    : generatePath('/layouts/menus/:menuId', { menuId });

// Pages
export const getPageListPath = (searchParams?: SearchParams): To => {
  const pathname = '/layouts/pages';

  return searchParams
    ? { pathname, search: queryString.stringify(searchParams, { arrayFormat: 'bracket' }) }
    : pathname;
};

export const getCreatePagePath = () => '/layouts/pages/create';
export const getPagePath = (pageId: string) => generatePath('/layouts/pages/:pageId', { pageId });

// Collections
export const getCollectionListPath = () => '/layouts/collections';

export const getCollectionPath = (collectionId: string) =>
  generatePath('/layouts/collections/:collectionId', { collectionId });

// METADATA

export const getWorkflowsPath = () => '/workflows';

export const getMetadataValidationPath = (tab: undefined | ValidationSeverity = 'error') =>
  `/metadata-validation/${tab}s`;

export const getDuplicateResolutionsPath = () => '/duplicate-resolution';
export const getDuplicateResolutionPath = (masterId: string) =>
  generatePath('/duplicate-resolution/:masterId', { masterId });

export const getEpgPath = () => '/epg';
export const getChannelEpgPath = (channelId: string) =>
  generatePath('/epg/:channelId', { channelId });

export const getContentStatisticsPath = (
  tab: ContentStatisticsPageTab | undefined = defaultContentStatisticsPageTab
) => generatePath('/statistics/content/:tab', { tab });

export const getQueryLogsPath = () => '/statistics/query-logs';
export const getTitlePopularityPath = () => '/statistics/title-popularity';
export const getFavoritesInsightsPath = () => '/statistics/favorites-insights';
export const getFocStatisticsPath = () => '/statistics/foc';

export const getSimPath = () => '/simulations';
export const getSimTabPath = (tab: SimTab) => generatePath('/simulations/:tab', { tab });

// FREEDOM OF CHOICE
export const getProvidersPath = () => '/content/providers';
export const getChannelsPath = () => '/content/channels';
export const getFastTrackPath = () => '/operations/fast-track';
export const getDevicesPath = () => '/operations/devices';
export const getFocSubscribersPath = () => '/operations/foc-subscribers';
export const getFastTrackChannelsPath = () => '/operations/fast-track/channels';
export const getChannelNewPath = () => '/content/channels/new';
export const getChannelPath = (id: string) => generatePath('/content/channels/:id', { id });
export const getContentPackagesPath = () => '/content/packages';
export const getContentPackageNewPath = () => '/content/packages/new';
export const getContentPackagePath = (id: string) => generatePath('/content/packages/:id', { id });
export const getTierPackagesPath = () => '/content/tier-packages';
export const getTierPackageNewPath = () => '/content/tier-packages/new';
export const getTierPackagePath = (id: string) =>
  generatePath('/content/tier-packages/:id', { id });
export const getBundlesPath = () => '/content/bundles';
export const getBundlePath = (id: string) => generatePath('/content/bundles/:id', { id });
export const getDaiRolloutsPath = () => '/operations/fast-track/rollouts';
export const getDaiRolloutNewPath = () => getDaiRolloutPath('new');
export const getDaiRolloutPath = (id: string, channelId?: string) =>
  `${getDaiRolloutsPath()}?${new URLSearchParams({
    rollout: id,
    ...(channelId ? { addChannel: channelId } : {}),
  })}`;

export const getClientConfigPath = () => '/client-config';

// DEV TOOLS
export const getDevComponentsPath = () => '/dev/components';
export const getDevChunkTestPath = () => '/dev/chunk-load-test';

/**
 * @param entityType Type of entity
 * @param id ID of entity. If ID is not provided, overview path for given entity type will be returned
 * @param isRootMenu Only applies to `entityType = 'menu'`
 * @returns Correct path according to specified attributes
 */
export const getEntityPath = (entityType: LayoutEntityType, id?: string, isRootMenu?: boolean) => {
  switch (entityType) {
    default:
    case 'collection':
      return id ? getCollectionPath(id) : getCollectionListPath();
    case 'menu':
      return id ? getMenuPath(id, isRootMenu) : getMenuListPath(isRootMenu);
    case 'page':
      return id ? getPagePath(id) : getPageListPath();
  }
};

/**
 * @param entityType Type of entity
 * @param id ID of entity. If ID is not provided, overview path for given entity type will be returned
 * @returns Preview path
 */
export const getPreviewPath = (entityType: LayoutEntityType, id: string, isRootMenu?: boolean) =>
  `${getEntityPath(entityType, id, isRootMenu)}/preview`;
